import axios from 'axios';
import { zynity_api } from '../../../../../config/external-routes';
import { DocumentLibraryPickerItem } from '../../../../ui/tools/DocumentLibraryPickerModal';

/**
 * Document Library association helpers for Strategies (Phase 7, §10, extended
 * 2026-08-27 to close the deliberate scope reductions noted in that phase's
 * writeup) — thin wrappers around the already-built, already-live
 * `/document-library`, `/document-library/contexts`, and
 * `/document-library/sync-context` endpoints. Context type/id conventions match
 * Yii's own `DocumentLibraryBridge` exactly (confirmed by reading
 * `StrategiesController.php`'s `buildStrategyItemDocumentContext()`/
 * `buildStrategyCommentDocumentContext()`): every Category, Discussion topic, and
 * Details/Milestone/Next-Step row uses `context_type: 'strategy_item'` keyed by
 * that row's own id; every individual Comment uses its own `context_type:
 * 'strategy_comment'` keyed by the comment's own id.
 */
export const STRATEGY_ITEM_CONTEXT_TYPE = 'strategy_item';
export const STRATEGY_COMMENT_CONTEXT_TYPE = 'strategy_comment';

const documentLibraryUrl = (organizationId: string, teamId: string) =>
    zynity_api.organization_team_document_library
        .replace('{organization_id}', organizationId)
        .replace('{team_id}', teamId);

export const fetchLibraryContextItems = async (
    organizationId: string,
    teamId: string,
    contextType: string,
    contextId: number
): Promise<DocumentLibraryPickerItem[]> => {
    const response = await axios.post(
        zynity_api.organization_team_document_library_contexts
            .replace('{organization_id}', organizationId)
            .replace('{team_id}', teamId),
        { contexts: [{ context_type: contextType, context_id: contextId }] }
    );

    const key = `${contextType}:${contextId}`;
    return response.data.documents?.[key] ?? [];
};

/**
 * Batched form of the above — one request for every id sharing the same
 * `contextType`, returning a map keyed by contextId. Used to display each
 * comment's already-attached library docs without a round-trip per comment.
 */
export const fetchLibraryContextItemsForMany = async (
    organizationId: string,
    teamId: string,
    contextType: string,
    contextIds: number[]
): Promise<Record<number, DocumentLibraryPickerItem[]>> => {
    if (contextIds.length === 0) {
        return {};
    }

    const response = await axios.post(
        zynity_api.organization_team_document_library_contexts
            .replace('{organization_id}', organizationId)
            .replace('{team_id}', teamId),
        { contexts: contextIds.map((contextId) => ({ context_type: contextType, context_id: contextId })) }
    );

    const documents: Record<string, DocumentLibraryPickerItem[]> = response.data.documents ?? {};
    const result: Record<number, DocumentLibraryPickerItem[]> = {};
    for (const contextId of contextIds) {
        result[contextId] = documents[`${contextType}:${contextId}`] ?? [];
    }
    return result;
};

export const syncLibraryContextItems = async (
    organizationId: string,
    teamId: string,
    contextType: string,
    contextId: number,
    contextLabel: string,
    itemIds: number[]
): Promise<void> => {
    await axios.post(
        zynity_api.organization_team_document_library_sync_context
            .replace('{organization_id}', organizationId)
            .replace('{team_id}', teamId),
        {
            context_type: contextType,
            context_id: contextId,
            context_label: contextLabel,
            item_ids: itemIds,
        }
    );
};

/** Matches Yii's `DocumentLibraryBridge::resolveLinkType()` exactly. */
const resolveLinkType = (url: string): 'link' | 'external_doc' => {
    try {
        const hostname = new URL(url).hostname.toLowerCase();
        if (hostname.includes('docs.google.com') || hostname.includes('drive.google.com')) {
            return 'external_doc';
        }
    } catch {
        // Not a parseable absolute URL — fall through to the generic link type.
    }
    return 'link';
};

/** Matches Yii's `DocumentLibraryBridge::deriveTitleFromUrl()` exactly. */
const deriveTitleFromUrl = (url: string): string => {
    try {
        const parsed = new URL(url);
        const segments = parsed.pathname.split('/').filter(Boolean);
        const basename = segments.length > 0 ? segments[segments.length - 1] : '';
        if (basename !== '') {
            return decodeURIComponent(basename);
        }
        return parsed.hostname;
    } catch {
        return url;
    }
};

/**
 * Shadow-indexes a typed URL into the team's Document Library, matching Yii's
 * `DocumentLibraryBridge::syncContext()` — every raw link a user types into any
 * Strategies Links field also becomes a reusable library item, findable from any
 * other "Choose from Library" picker afterward. Returns the new (or matched
 * existing, per the backend's own dedupe-by-URL logic) item so it can be merged
 * into the caller's own library-context sync in the same request round.
 */
export const registerLinkInLibrary = async (
    organizationId: string,
    teamId: string,
    url: string
): Promise<DocumentLibraryPickerItem> => {
    const response = await axios.post(documentLibraryUrl(organizationId, teamId), {
        type: resolveLinkType(url),
        title: deriveTitleFromUrl(url),
        external_url: url,
    });
    return response.data.item;
};

/**
 * Shadow-indexes an uploaded file into the team's Document Library, matching
 * Yii's `DocumentLibraryBridge::syncContext()`. The classic Strategies upload
 * endpoints (`.../attachments/uploads`) *move* their temp file into
 * `strategies/teams/{team_id}` as part of creating the StrategyUpload row, so
 * the same temp reference can't be reused here — this uploads the file a
 * second, independent time so the Document Library gets its own temp file to
 * move into `document_library/teams/{team_id}`.
 */
export const registerUploadInLibrary = async (
    organizationId: string,
    teamId: string,
    file: File,
    displayName: string
): Promise<DocumentLibraryPickerItem> => {
    const formData = new FormData();
    formData.append('file', file, file.name);
    const tempResponse = await axios.post(zynity_api.global_upload_temp_files, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });

    const response = await axios.post(documentLibraryUrl(organizationId, teamId), {
        type: 'internal_doc',
        title: displayName,
        display_name: displayName,
        file_name: tempResponse.data.file_name,
    });
    return response.data.item;
};
